/**
  * @class mmodReport
  * @description
  * Module for displaying reports with filtering options and data export capabilities.
  */

import mmodModule from 'mmodModule'

export default class mmodReport extends mmodModule {
  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates an instance of the class.
  * @param {string} [id] - ID under which the object will listen for messages
  * @param {string} [cfg] - object initial configuration
  * @param {string} [cfg_schema] - additional schema properties from derived classes
  * @param {string} [dependencies] - additional dependencies from derived classes
  */
  constructor(id, cfg, cfg_schema, dependencies) {
    dependencies = _.union([], dependencies)
    cfg_schema = _.extend({
      init: {
        type: 'object',
        default: { type: 'full' },
        description: 'Initial window setup; same structure as required by `mmWindowSet`',
      },
      suspended: {
        type: 'string',
        enum: ['', 'hidden', 'disabled'],
        description: 'how to report suspended state in mmStatus',
        default: 'hidden',
      },
      service: {
        type: 'string',
        description: 'Server service to fetch report data from',
      },
    }, cfg_schema)
    super(id, cfg, cfg_schema, dependencies)
  } // constructor

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * initialization of GUI components
  */
  async initWindow() {
    this.toggleModuleVisibility()
    this.mmStart()
  } // initWindow

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * shows/hides module from application bars according to workflow availability
  */
  toggleModuleVisibility() {
    if(this.elFilter) {
      // show module
      this.publish('#ALL', 'mmStatus', { status: 'ok' })
    }else{
      // hide module
      this.publish('#ALL', 'mmStatus', { status: this.config.suspended })
    }
  } // toggleModuleVisibility

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * information on changed user authorization; user_id is empty if not logged
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.app_id] - unique application ID
  * @param {integer} [params.user_id] - user's unique ID
  * @param {string} [params.login] - user's login name
  * @param {string} [params.callsign] - user's callsign
  * @param {object} [params.roles] - roles assigned to user
  * @param {string} [params.privatekey] - user's private key encrypted by his password
  * @param {string} [params.error] - error message if login failed
  */
  mmUserLogged(sender, params) {
    if(!params.user_id || params.user_id === '') return
    this.publish(this.config.service, 'mmModelGet')
  } // mmUserLogged

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * workflow model definition
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.id] - workflow ID
  * @param {integer} [params.version] - model version
  * @param {string} [params.icon] - SVG icon
  * @param {string} [params.md5] - MD5 checksum
  * @param {string} [params.id_generator] - indicates who and how generates id
  * @param {string} [params.sqlview] - definition of SQL query to retrieve read-only workflow
  * @param {string} [params.sqlview_log] - definition of SQL query to retrieve read-only workflow logs
  * @param {string} [params.notifications] - indicates whether to notify on object changes
  * @param {object} [params.fields] - workflow variables
  * @param {object} [params.sql_queries] - SQL queries used for calculation of fields
  * @param {object} [params.forms] - workflow GUI forms
  * @param {string} [params.translations] - workflow translations as CSV
  * @param {string} [params.scripts] - workflow scripts
  * @param {string} [params.styles] - workflow CSS styles
  * @param {array} [params.acls] - access control list
  * @param {object} [params.states] - workflow states and their actions
  */
  async mmModel(sender, params) {
    if(params.forms) {
      // add custom report translations
      if(params.translations) {
        LANG.loadTranslation(params.translations)
        const alt_translations = [
          /\n(\w+\.Menu);/.exec(params.translations)?.[1],
          /\n(\w+\.Title);/.exec(params.translations)?.[1],
        ].filter(t=>t)
        if(alt_translations.length>0) {
          LANG.copyTranslation(this.listener_id, [this.listener_id, ...alt_translations])
        }
      }

      // ensure that "key" attribute is present in each form
      for(const key in params.forms) {
        params.forms[key].key = key
      }

      // update GUI forms if changed
      if(!this.forms || !_.isEqual(this.forms, params.forms)) {
        this.forms = params.forms
        const form = {
          tag: 'mele-view',
          layout: 'sidesplit',
          views: 'filter,list',
          items: Object.values(this.forms),
        }
        this.loadForm(form)

        // cache references to important forms' elements
        this.elFilter = this.el.find('@filter')
        this.elList = this.el.find('@list')
      }

      // update module icon
      if(params.icon) {
        this.el.setSvgIcon(this.listener_id, params.icon)
      }

      // update module CSS styles
      const styles_id = 'css_'+this.listener_id
      let elStyles = document.getElementById(styles_id)
      if(params.styles) {
        if(!elStyles) {
          elStyles = this.el.createElementFast('style')
          elStyles.type = 'text/css'
          elStyles.id = styles_id
          document.head.appendChild(elStyles)
        }
        elStyles.innerHTML = params.styles
      }else if(elStyles) {
        // remove old styles
        elStyles.parentNode.removeChild(elStyles)
      }
    }else {
      // deleted model (i.e. module should not be accessible by user)
      delete this.elFilter
      delete this.elList
      this.el.deleteItems()
    }

    this.toggleModuleVisibility()
    this.el.layoutChanged()
  } // mmModel

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Sends by &lt;mele-button> to parent module when a button is clicked
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.holder] - key attribute of button's parent container
  * @param {string} params.button - key attribute of the button
  * @param {integer} [params.count] - number of times button was clicked
  * @param {boolean} [params.long] - shows if press was long or not
  */
  async mmButtonClicked(sender, params) {
    const { holder, button } = params
    if(holder === 'list') {
      const format = String(button).toLowerCase()
      if(format === 'csv') {
        this.elList.exportCSV('report.csv', ',')
      }else if(format === 'json') {
        this.elList.exportJSON('report.json')
      }else if(format === 'xls' || format === 'xlsx') {
        this.elList.exportXLSX('report.xlsx')
      }else if(format === 'xml') {
        this.elList.exportXML('report.xml')
      }else {
        this.modalError('Nepodporovaný formát exportu: '+format)
      }
    }
  } // mmButtonClicked

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
   * @description
   * Sends by <mele-list> when selected item change
   * @param {object} sender - message sender
   * @param {object} params - message parameters
   * @param {string} [params.holder] - key attribute of the list
   * @param {string} params.selected - key attribute of the selected items separated by ∫
   * @param {string} [params.old] - key attribute of previously selected item
   * @param {boolean} [params.interactive] - indicates if this was user activity
   */
  mmListSelected(sender, params) {
    const { selected } = params
    if(!selected) return
    const detail = this.forms?.detail
    if(!detail) return
    const data = this.elList.getItem(selected)?.data
    if(!data) return
    const modal = this.modalOpen(detail)
    modal.setValues(data)
  } // mmListSelected

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
   * @description
   * Sent by &lt;mele-container> or derived class to parent module when form is submitted
   * @param {object} sender - message sender
   * @param {object} params - message parameters
   * @param {string} params.holder - key attribute of the form
   * @param {string} [params.button] - key attribute of the button
   * @param {string} [params.datakey] - key attribute of the content
   * @param {object} params.values - hash of key:value of contained editable inputs
   * @param {boolean} [params.invalid] - true if any of form's inputs is invalid
   * @param {boolean} [params.conflict] - true if any of form's inputs has conflict
   */
  async mmFormSubmit(sender, params) {
    const { holder } = params
    if(holder === 'filter') {
      // fetch data and display in table
      const form = this.el.getValues()
      form.format = 'rows'
      const orders = []
      for(const key in form) {
        const idx = /^sort(\d+)$/.exec(key)?.[1]
        if(idx>0) {
          orders.push({ idx: parseInt(idx), val: form[key] })
          delete form[key]
        }
      }
      orders.sort((a, b)=>a.idx - b.idx)
      form.order = orders.map(o=>o.val).join('∫')
      try{
        this.el.waiting = true
        const { params: { data } } = await this.request(this.config.service, 'mmFetch', { params: form }, 'mmData')
        const c = data.length
        const summary = c === 1 ? 'Nájdený 1 záznam' : c >=2 && c <=4 ? `Nájdené ${c} záznamy` : `Nájdených ${c} záznamov`
        this.elList.footers = [{
          tag: 'mele-footer',
          html: summary,
        }]
        const items = []
        const columns = this.elList.columns.map(col=>col.key)
        for(let i=0; i<data.length; i++) {
          items.push({
            key: i,
            data: Object.fromEntries(columns.map((col, j)=>[col, data[i][j]])),
          })
        }
        this.elList.setItems(items)
        setTimeout(()=>this.elList.refreshTableWidths(), 10)
      }catch(err) {
        this.elStatus.value = 'Chyba pri načítaní dát: ' + err.message
      }
      this.el.waiting = false
    }
  } // mmFormSubmit
} // mmodReport
